/**
 * Host-side layout override, the `data-layout` attribute on the embed tag.
 *
 * The banner layout normally comes from the domain's published banner config.
 * One registered domain can serve pages that need different layouts, for
 * example a marketing site where a bottom bar is right and an authenticated
 * app where consent must be resolved before any workflow starts, so it is
 * shown as a centred modal. The host page asks for that per page with
 * `data-layout` on the Beacon script tag:
 *
 *   <script src=".../pritect-beacon-bootstrap-v1.<hash>.min.js" ...
 *           data-domain-id="..." data-layout="centered_modal"></script>
 *
 * Precedence: a valid `data-layout` wins over the published config (and over
 * an A/B variant's layout), which wins over the built-in fallback's
 * bottom_bar. A missing, empty or unknown value is ignored and the published
 * config applies, so a typo can never leave the site with no banner. Only the
 * presentation moves: copy, categories, defaults and what is recorded are the
 * published config's, whatever the layout.
 *
 * The attribute is read by the core, not by the bootstrap or loader, and the
 * core finds the tag itself. That keeps the bootstrap's bytes, and therefore
 * every SRI pin on it, unchanged, and means a site already embedding any
 * published bootstrap gets the override as soon as the release pointer names a
 * core that carries it.
 *
 * Single-page apps: the attribute is watched, not read once. Changing it on a
 * route change (`script.dataset.layout = "centered_modal"`, or removing it)
 * re-lays out a banner or preferences panel that is on screen and applies to
 * every later one. A panel that is re-laid out starts again from its first
 * layer; nothing is recorded by the switch.
 */

export const BANNER_LAYOUTS = [
  "bottom_bar",
  "top_bar",
  "bottom_left_popup",
  "bottom_right_popup",
  "centered_modal",
  "full_screen",
] as const;

export type BannerLayout = (typeof BANNER_LAYOUTS)[number];

/** A known layout, or null for anything else (missing, empty, unknown, wrong case). */
export function parseLayout(value: unknown): BannerLayout | null {
  return typeof value === "string" && (BANNER_LAYOUTS as readonly string[]).includes(value)
    ? (value as BannerLayout)
    : null;
}

type Listener = () => void;

let override: BannerLayout | null = null;
let observer: MutationObserver | null = null;
const listeners = new Set<Listener>();

/** The host's requested layout, or null when it requested none (or an invalid one). */
export function hostLayout(): BannerLayout | null {
  return override;
}

/** The layout to render: the host override when there is one, else the configured layout. */
export function effectiveLayout(configured: string): string {
  return override ?? configured;
}

/**
 * Subscribe to override changes. Returns the unsubscribe function. Renderers
 * subscribe only while they are on screen.
 */
export function onHostLayoutChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setOverride(next: BannerLayout | null): void {
  if (next === override) return;
  override = next;
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      /* one failing renderer must not stop the others */
    }
  }
}

/**
 * The embed tag for this domain, found the way getScriptConfig() finds it: the
 * executing script when it carries data-domain-id (a version-pinned embed),
 * otherwise a Beacon tag carrying this domain id (the bootstrap or loader that
 * injected this core). Matching on the domain id keeps the override bound to
 * the tag the running config came from. Duplicated rather than shared with
 * scriptConfig.ts on purpose, so the bootstrap bundle, and the SRI pins on it,
 * do not change.
 */
export function findEmbedScript(domainId: string): HTMLScriptElement | null {
  const current = document.currentScript instanceof HTMLScriptElement ? document.currentScript : null;
  if (current?.dataset["domainId"] === domainId) return current;
  const tags = document.querySelectorAll<HTMLScriptElement>('script[src*="pritect-beacon"][data-domain-id]');
  return Array.from(tags).find((tag) => tag.dataset["domainId"] === domainId) ?? null;
}

/**
 * Read `data-layout` from the embed tag now and keep following it. Call once,
 * synchronously at core boot. Safe to call again: it re-binds to the tag
 * given (tests, or a host that replaced the tag).
 */
export function watchHostLayout(script: HTMLScriptElement | null): void {
  observer?.disconnect();
  observer = null;
  setOverride(parseLayout(script?.dataset["layout"]));
  if (!script || typeof MutationObserver !== "function") return;
  observer = new MutationObserver(() => setOverride(parseLayout(script.dataset["layout"])));
  observer.observe(script, { attributes: true, attributeFilter: ["data-layout"] });
}

/** Test helper: forget the override, the observer and every listener. */
export function resetHostLayoutForTests(): void {
  observer?.disconnect();
  observer = null;
  override = null;
  listeners.clear();
}
